import React from 'react';

import { fadeDurationSelector } from '@redux/videoPlayerResources/selectors';
import { useSelector } from 'react-redux';

import type { Block as BlockType, Sentence as SentenceType } from 'shared-types/lyrics';
import type { SwipeColors } from 'types/swipeColors';

import { BlockContainer, SentenceContainer } from './Block.style';
import { LYRICS_TIME_TO_MS_RATIO } from '../Lyrics/Lyrics.utils';
import Sentence from '../Sentence';
import { getSentenceAnimationsDelayInMs } from '../Sentence/Sentence.utils';

const Block: React.FC<{
  block: BlockType;
  blockIndex: number;
  isPlaying: boolean;
  lastManualUpdateTimeInMs: number;
  swipeColors: SwipeColors;
  wordOutlineFontSize: number;
  cursorAreaWidthInPx: number;
  cursorAreaRightSpacingInPx: number;
}> = ({
  block,
  blockIndex,
  isPlaying,
  lastManualUpdateTimeInMs,
  swipeColors,
  wordOutlineFontSize,
  cursorAreaWidthInPx,
  cursorAreaRightSpacingInPx,
}) => {
  const blockStartInMs = block.blockStart * LYRICS_TIME_TO_MS_RATIO;
  const blockStartOrLastManualTimeUpdate = Math.max(blockStartInMs, lastManualUpdateTimeInMs);
  const fadeDuration = useSelector(fadeDurationSelector);

  return (
    <BlockContainer>
      {block.blockSentences.map((sentence: SentenceType, sentenceIndex: number) => {
        const [sentenceStartAnimationDelay, sentenceEndAnimationDelay] =
          getSentenceAnimationsDelayInMs(sentence, blockStartOrLastManualTimeUpdate, fadeDuration);

        return (
          <SentenceContainer
            // We use the sentence index as key here because
            // - we do not add/change/remove sentences after they are rendered
            // - combinations of sentence owner, start, end and words do not enable us
            // to create a unique key (there can be two different sentences with exactly
            // those same combinations)
            key={`${blockIndex}-${
              // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
              sentenceIndex
            }`}
            style={{
              animationDelay: `${sentenceStartAnimationDelay}ms, ${sentenceEndAnimationDelay}ms`,
              animationPlayState: isPlaying ? 'running' : 'paused',
            }}
            {...{ fadeDuration }}
          >
            <Sentence
              words={sentence.sentenceWords}
              isPlaying={isPlaying}
              blockStartOrLastManualTimeUpdate={blockStartOrLastManualTimeUpdate}
              swipeColor={swipeColors[sentence.sentenceOwner]}
              hasCursor={sentence.sentenceHasCursor}
              wordOutlineFontSize={wordOutlineFontSize}
              cursorAreaWidthInPx={cursorAreaWidthInPx}
              cursorAreaRightSpacingInPx={cursorAreaRightSpacingInPx}
            />
          </SentenceContainer>
        );
      })}
    </BlockContainer>
  );
};

export default React.memo(Block);
